/**
 * SEO Navigation Links
 * 
 * Reusable component that displays clickable links to SEO pages.
 * Every click leads to a real, indexable page - zero dead clicks.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MapPin, Briefcase, Wrench, Car, Home, Globe, ChevronRight, 
  Building2, Users, Truck, Hammer, Zap, Droplets, Paintbrush
} from 'lucide-react';

// Country data with flags and SEO slugs
const COUNTRIES = [
  { code: 'FI', slug: 'finland', flag: '🇫🇮', nameKey: 'countries.FI' },
  { code: 'EE', slug: 'estonia', flag: '🇪🇪', nameKey: 'countries.EE' },
  { code: 'SE', slug: 'sweden', flag: '🇸🇪', nameKey: 'countries.SE' },
  { code: 'NO', slug: 'norway', flag: '🇳🇴', nameKey: 'countries.NO' },
  { code: 'DE', slug: 'germany', flag: '🇩🇪', nameKey: 'countries.DE' },
  { code: 'DK', slug: 'denmark', flag: '🇩🇰', nameKey: 'countries.DK' },
];

// Job categories with icons and SEO slugs
const JOB_CATEGORIES = [
  { value: 'construction', icon: Hammer, labelKey: 'categories.construction' },
  { value: 'electrical', icon: Zap, labelKey: 'categories.electrical' },
  { value: 'plumbing', icon: Droplets, labelKey: 'categories.plumbing' },
  { value: 'logistics', icon: Truck, labelKey: 'categories.logistics' },
  { value: 'cleaning', icon: Home, labelKey: 'categories.cleaning' },
  { value: 'welding', icon: Wrench, labelKey: 'categories.welding' },
  { value: 'carpentry', icon: Hammer, labelKey: 'categories.carpentry' },
  { value: 'painting', icon: Paintbrush, labelKey: 'categories.painting' },
];

// Service categories
const SERVICE_CATEGORIES = [
  { value: 'construction', icon: '🏗️', labelKey: 'services.categories.construction' },
  { value: 'electrical', icon: '⚡', labelKey: 'services.categories.electrical' },
  { value: 'plumbing', icon: '🔧', labelKey: 'services.categories.plumbing' },
  { value: 'cleaning', icon: '🧹', labelKey: 'services.categories.cleaning' },
  { value: 'moving', icon: '📦', labelKey: 'services.categories.moving' },
  { value: 'renovation', icon: '🏠', labelKey: 'services.categories.renovation' },
];

// Auto repair services
const AUTO_SERVICES = [
  { value: 'oil_change', icon: '🛢️', labelKey: 'auto.services.oilChange' },
  { value: 'brake_repair', icon: '🔧', labelKey: 'auto.services.brakeRepair' },
  { value: 'tire_service', icon: '🛞', labelKey: 'auto.services.tireService' },
  { value: 'diagnostics', icon: '🔍', labelKey: 'auto.services.diagnostics' },
];

// Cities by country
const CITIES = {
  finland: ['helsinki', 'espoo', 'tampere', 'turku', 'oulu'],
  estonia: ['tallinn', 'tartu', 'parnu', 'narva'],
  sweden: ['stockholm', 'gothenburg', 'malmo', 'uppsala'],
  norway: ['oslo', 'bergen', 'trondheim', 'stavanger'],
};

/**
 * Country Links - Grid of clickable country badges
 */
export const CountryLinks = ({ basePath = '/jobs', className = '' }) => {
  const { t } = useLanguage();
  
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {COUNTRIES.map((country) => (
        <Link
          key={country.code}
          to={`${basePath}/${country.slug}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border hover:border-brand hover:bg-brand/5 transition-all text-sm"
          data-testid={`country-link-${country.slug}`}
        >
          <span>{country.flag}</span>
          <span>{t(country.nameKey)}</span>
        </Link>
      ))}
    </div>
  );
};

/**
 * Category Links - Grid of clickable category chips
 */
export const CategoryLinks = ({ basePath = '/jobs', country = null, className = '' }) => {
  const { t } = useLanguage();
  
  const buildPath = (categorySlug) => {
    if (country) {
      return `${basePath}/${country}/${categorySlug}`;
    }
    return `${basePath}/finland/${categorySlug}`;
  };
  
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {JOB_CATEGORIES.map((category) => (
        <Link
          key={category.value}
          to={buildPath(category.value)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hover:border-brand hover:bg-brand/5 transition-all text-sm group"
          data-testid={`category-link-${category.value}`}
        >
          <category.icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-brand transition-colors" />
          <span>{t(category.labelKey)}</span>
        </Link>
      ))}
    </div>
  );
};

/**
 * City Links - Grid of clickable city chips for a country
 */
export const CityLinks = ({ country = 'finland', basePath = '/services', className = '' }) => {
  const { t } = useLanguage();
  const cities = CITIES[country] || [];
  
  const formatCityName = (slug) => {
    return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };
  
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {cities.map((city) => (
        <Link
          key={city}
          to={`${basePath}/${country}/${city}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hover:border-brand hover:bg-brand/5 transition-all text-sm"
          data-testid={`city-link-${city}`}
        >
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{formatCityName(city)}</span>
        </Link>
      ))}
    </div>
  );
};

/**
 * Browse By Country Card - Full card with country grid
 */
export const BrowseByCountryCard = ({ 
  title, 
  basePath = '/jobs', 
  showCategories = true,
  className = '' 
}) => {
  const { t } = useLanguage();
  
  return (
    <Card className={`${className}`} data-testid="browse-by-country-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-brand" />
          {title || t('common.browseByCountry')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {COUNTRIES.map((country) => (
            <Link
              key={country.code}
              to={`${basePath}/${country.slug}`}
              className="flex flex-col items-center p-3 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid={`country-card-${country.slug}`}
            >
              <span className="text-2xl mb-1">{country.flag}</span>
              <span className="text-sm font-medium group-hover:text-brand transition-colors">
                {t(country.nameKey)}
              </span>
            </Link>
          ))}
        </div>
        
        {showCategories && (
          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-3">{t('common.popularCategories')}</p>
            <CategoryLinks basePath={basePath} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Quick Navigation Grid - Compact grid with icons
 */
export const QuickNavGrid = ({ className = '' }) => {
  const { t } = useLanguage();
  
  const navItems = [
    { path: '/jobs', icon: Briefcase, labelKey: 'nav.jobs', color: 'text-blue-500' },
    { path: '/services/finland', icon: Wrench, labelKey: 'nav.services', color: 'text-green-500' },
    { path: '/autoremont/tallinn', icon: Car, labelKey: 'nav.autoRepair', color: 'text-orange-500' },
    { path: '/rentals', icon: Building2, labelKey: 'nav.rentals', color: 'text-purple-500' },
    { path: '/subcontracting', icon: Users, labelKey: 'nav.subcontracting', color: 'text-pink-500' },
  ];
  
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 ${className}`}>
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
          data-testid={`quick-nav-${item.path.replace(/\//g, '-')}`}
        >
          <item.icon className={`h-8 w-8 ${item.color} mb-2 group-hover:scale-110 transition-transform`} />
          <span className="text-sm font-medium text-center">{t(item.labelKey)}</span>
        </Link>
      ))}
    </div>
  );
};

/**
 * Service Categories Grid - For services page
 */
export const ServiceCategoriesGrid = ({ country = 'finland', city = null, className = '' }) => {
  const { t } = useLanguage();
  
  const buildPath = (categorySlug) => {
    if (city) {
      return `/services/${country}/${city}/${categorySlug}`;
    }
    return `/services/${country}/${categorySlug}`;
  };
  
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 ${className}`}>
      {SERVICE_CATEGORIES.map((category) => (
        <Link
          key={category.value}
          to={buildPath(category.value)}
          className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
          data-testid={`service-category-${category.value}`}
        >
          <span className="text-3xl mb-2">{category.icon}</span>
          <span className="text-sm font-medium text-center group-hover:text-brand transition-colors">
            {t(category.labelKey)}
          </span>
        </Link>
      ))}
    </div>
  );
};

/**
 * Auto Service Grid - For auto repair page
 */
export const AutoServiceGrid = ({ city = 'tallinn', className = '' }) => {
  const { t } = useLanguage();
  
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 ${className}`}>
      {AUTO_SERVICES.map((service) => (
        <Link
          key={service.value}
          to={`/autoremont/${city}/${service.value}`}
          className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
          data-testid={`auto-service-${service.value}`}
        >
          <span className="text-3xl mb-2">{service.icon}</span>
          <span className="text-sm font-medium text-center group-hover:text-brand transition-colors">
            {t(service.labelKey)}
          </span>
        </Link>
      ))}
    </div>
  );
};

export default {
  CountryLinks,
  CategoryLinks,
  CityLinks,
  BrowseByCountryCard,
  QuickNavGrid,
  ServiceCategoriesGrid,
  AutoServiceGrid,
};
